import { useEffect } from "react"
import { HandoffNotificationDTO, useChatStore } from "@/features/chat/hooks/use-chat-store"
import { soundAlertStore } from "@/features/chat/hooks/use-sound-alert-store"
import { MessageDirection, MessageNotificationDTO } from "@/hooks/types"
import { useWebSocketCallback } from "@/hooks/use-websocket-channel"
import { installNotificationAudioUnlock, playNotificationChime } from "@/lib/notification-sound"

/**
 * Plays a chime when a conversation needs attention in the Pendentes tab:
 * a new inbound message on a waiting conversation, or a conversation
 * entering handoff (which moves it to Pendentes).
 *
 * Must be mounted once, inside the Ably providers.
 */
export function SoundAlertListener() {
  useEffect(() => {
    installNotificationAudioUnlock()
  }, [])

  useWebSocketCallback("message.new", (data: MessageNotificationDTO) => {
    if (data.message.direction !== MessageDirection.INBOUND) return

    const { chats } = useChatStore.getState()
    const chat = chats.find(candidate => candidate.id === data.conversation.id)
    if (chat?.status !== "waiting") return

    playChimeUnlessViewing(data.conversation.id)
  })

  useWebSocketCallback("conversation.handoff", (data: HandoffNotificationDTO) => {
    if (!data.is_active) return

    const { chats } = useChatStore.getState()
    const chat = chats.find(candidate => candidate.id === data.conversation_id)
    // Already in Pendentes: a handoff renewal, not a transition into the tab.
    if (chat?.status === "waiting") return

    playChimeUnlessViewing(data.conversation_id)
  })

  return null
}

function playChimeUnlessViewing(conversationId: string) {
  if (!soundAlertStore.getState().soundAlertEnabled) return

  const { activeChat } = useChatStore.getState()
  const operatorIsViewingConversation = activeChat === conversationId && !document.hidden
  if (operatorIsViewingConversation) return

  playNotificationChime()
}
